/**
 * Workflow v2 — workflow-wide status polling.
 *
 * Thin specialization of the generic `usePolling` primitive that
 * polls `GET /workflow/{thread_id}/status` and forwards snapshots
 * to the consumer (typically a reducer's `HYDRATE_FROM_BACKEND`
 * dispatch).
 *
 * Active when any agent is in-flight (`pending` / `in_progress`)
 * OR backend reports `current_agent` set. Idles when all agents
 * are in terminal states (no `current_agent` AND no in-flight
 * agents) — resumes via `refetch()`.
 *
 * Returns `{ refetch, error }`.
 */
import { useCallback } from 'react';
import { usePolling, type PollingState } from './usePolling';
import { getWorkflowStatus } from '../../../api/workflow';
import type { BackendWorkflowProgress } from '../state/workflowV2Types';

const IN_FLIGHT_STATUSES = new Set(['in_progress', 'pending']);

function hasActiveWork(workflowProgress: BackendWorkflowProgress | null | undefined): boolean {
  if (!workflowProgress) return false;
  if (workflowProgress.current_agent) return true;
  const ap = workflowProgress.agent_progress || {};
  for (const key of Object.keys(ap)) {
    if (IN_FLIGHT_STATUSES.has((ap as any)[key]?.status)) return true;
  }
  return false;
}

export interface UseWorkflowStatusPollingInput {
  threadId: string | null | undefined;
  onSnapshot?: (snapshot: BackendWorkflowProgress | null) => void;
  intervalMs?: number;
}

export function useWorkflowStatusPolling({
  threadId,
  onSnapshot,
  intervalMs,
}: UseWorkflowStatusPollingInput): PollingState {
  const fetcher = useCallback(
    (signal: AbortSignal): Promise<BackendWorkflowProgress | null> =>
      threadId
        ? (getWorkflowStatus(threadId, { signal }) as unknown as Promise<BackendWorkflowProgress | null>)
        : Promise.resolve(null),
    [threadId],
  );

  return usePolling<BackendWorkflowProgress | null>({
    fetcher,
    isActive: hasActiveWork,
    onData: onSnapshot,
    intervalMs,
    enabled: !!threadId,
  });
}
